import { getMessaging, getToken, isSupported, Messaging, onMessage } from 'firebase/messaging';
import { firebaseApp, keyPushNotificationWeb } from './firebase';
import { UserService } from './modules/configuracoes/usuario/Service';

interface RequestAndSaveTokenOptions {
    autoPrompt?: boolean;
    userId?: string | null;
}

export class PushNotification {
    private static inFlight: Promise<NotificationPermission | null> | null = null;
    private static foregroundListenerInitialized = false;
    private static messagingInstance: Messaging | null = null;
    private static autoPromptStorageKeyPrefix = 'push_notification:auto_prompt:last_date';

    private static getServiceWorkerScriptUrl(registration: ServiceWorkerRegistration) {
        return registration.active?.scriptURL || registration.waiting?.scriptURL || registration.installing?.scriptURL || '';
    }

    private static async getMessagingInstance() {
        if (PushNotification.messagingInstance) {
            return PushNotification.messagingInstance;
        }

        const messagingSupported = await isSupported();
        if (!messagingSupported) {
            return null;
        }

        PushNotification.messagingInstance = getMessaging(firebaseApp);
        return PushNotification.messagingInstance;
    }

    private static async ensureFirebaseServiceWorker() {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const rootScope = `${window.location.origin}/`;

        for (const registration of registrations) {
            const scriptUrl = PushNotification.getServiceWorkerScriptUrl(registration);
            const isRootScope = registration.scope === rootScope;
            const isFirebaseMessagingSw = scriptUrl.includes('/firebase-messaging-sw.js');

            if (isRootScope && scriptUrl && !isFirebaseMessagingSw) {
                await registration.unregister();
            }
        }

        return navigator.serviceWorker.register('/firebase-messaging-sw.js');
    }

    private static initForegroundListener(messaging: Messaging) {
        if (PushNotification.foregroundListenerInitialized) return;

        onMessage(messaging, (payload) => {
            const notification = payload?.notification || {};
            const data = payload?.data || {};

            if (Notification.permission !== 'granted') return;

            const title = notification?.title || data?.title;
            const body = notification?.body || data?.body || '';
            if (!title && !body) return;

            const image =
                notification?.image ||
                // @ts-ignore compat com payload legado
                notification?.imageUrl ||
                data?.image ||
                data?.imageUrl;

            new Notification(`Salonflow - ${title || 'Notificação'}`, {
                body,
                icon: '/icon.ico',
                ...(image ? { image } : {}),
            });
        });

        PushNotification.foregroundListenerInitialized = true;
    }

    private static getLocalDateKey(date = new Date()) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    private static getAutoPromptStorageKey(userId?: string | null) {
        return `${PushNotification.autoPromptStorageKeyPrefix}:${userId || 'anonymous'}`;
    }

    private static wasAutoPromptedToday(userId?: string | null) {
        try {
            const key = PushNotification.getAutoPromptStorageKey(userId);
            const lastPromptDate = localStorage.getItem(key);
            return lastPromptDate === PushNotification.getLocalDateKey();
        } catch {
            return false;
        }
    }

    private static markAutoPromptedToday(userId?: string | null) {
        try {
            const key = PushNotification.getAutoPromptStorageKey(userId);
            localStorage.setItem(key, PushNotification.getLocalDateKey());
        } catch {
            // Ignora falhas de storage para nao quebrar o fluxo de notificacao.
        }
    }

    static async requestAndSaveToken(options?: RequestAndSaveTokenOptions): Promise<NotificationPermission | null> {
        const { autoPrompt = false, userId } = options || {};

        if (PushNotification.inFlight) {
            return PushNotification.inFlight;
        }

        PushNotification.inFlight = (async () => {
            if (typeof window === 'undefined' || typeof Notification === 'undefined') {
                return null;
            }

            try {
                const currentPermission = Notification.permission;

                if (autoPrompt && currentPermission === 'default') {
                    if (PushNotification.wasAutoPromptedToday(userId)) {
                        return currentPermission;
                    }

                    PushNotification.markAutoPromptedToday(userId);
                }

                const permission =
                    currentPermission === 'default'
                        ? await Notification.requestPermission()
                        : currentPermission;

                if (permission !== 'granted') {
                    return permission;
                }

                if (!('serviceWorker' in navigator)) {
                    return permission;
                }

                const messaging = await PushNotification.getMessagingInstance();
                if (!messaging) {
                    return permission;
                }

                PushNotification.initForegroundListener(messaging);
                const serviceWorkerRegistration = await PushNotification.ensureFirebaseServiceWorker();

                const currentToken = await getToken(messaging, {
                    vapidKey: keyPushNotificationWeb,
                    serviceWorkerRegistration,
                });
                if (!currentToken) {
                    return permission;
                }

                const userService = new UserService();
                await userService.saveTokenPushNotification(currentToken, 'web');

                return permission;
            } catch {
                return null;
            } finally {
                PushNotification.inFlight = null;
            }
        })();

        return PushNotification.inFlight;
    }
}
